define('presentation/settings/billing/plans/viewModels/addPlanViewModel',[
    'i18next',
    'businessServices/router/router',
    'businessServices/state/modelStateObserver',
    'common/promises/promiseFactory',
    'constants/billingPackageTypeConstants',
    'presentation/common/actionModal/viewModels/actionModalViewModel',
    'presentation/common/modal',
    'presentation/settings/billing/plans/facades/addPlanFacade',
    'presentation/settings/billing/plans/viewModels/addCustomPlanActionModalViewModel',
    'presentation/settings/billing/plans/viewModels/addPlanActionModalViewModel',
    'settings/navigationConfiguration'
], function () {
    return function () {
        let self = this;

        const ActionModalConstructor = require('presentation/common/actionModal/viewModels/actionModalViewModel');
        const _actionModal = new ActionModalConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const ModelStateObserverConstructor = require('businessServices/state/modelStateObserver');
        const AddPlanActionModalViewModel = require('presentation/settings/billing/plans/viewModels/addPlanActionModalViewModel');
        const AddCustomPlanActionModalViewModel = require('presentation/settings/billing/plans/viewModels/addCustomPlanActionModalViewModel');

        const _i18n = require('i18next');
        const _billingPackageTypes = require('constants/billingPackageTypeConstants');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');

        let _facade = null;
        let _modalService = null;

        let _currentPackageId = null;
        let _isPackageOnboardingComplete = false;

        const FAQ_GROUP = {
            addingPlans: "addingPlans",
            changingPlans: "changingPlans",
            meteredBilling: "meteredBilling",
            removingPlans: "removingPlans"
        };

        const _buildFAQGroup = (type) => {
            let group = {
                title: ko.observable(""),
                messages: ko.observableArray([]),
            };
            group.title = ko.pureComputed(() => {
                return _i18n.t(`addPlan:${type}:title`);
            });
            group.messages = ko.pureComputed(() => {
                switch (type) {
                    case FAQ_GROUP.addingPlans:
                        return ['onboardingRequiredMessageOne', 'onboardingRequiredMessageTwo', 'onboardingRequiredMessageThree'];
                    case FAQ_GROUP.changingPlans:
                        return ['planChangeMessageOne', 'planChangeMessageTwo', 'planChangeMessageThree', 'planChangeMessageFour'];
                    case FAQ_GROUP.meteredBilling:
                        return ['meteredBillingMessageOne', 'meteredBillingMessageTwo'];
                    case FAQ_GROUP.removingPlans:
                        return ['removingPlanMessage'];
                    default:
                        return [];
                }
            });
            return group;
        };

        const _getPackageById = (packageId) => {
            const foundPackage = self.availablePackages().find((pkg) => pkg.packageId === packageId);
            return foundPackage ? foundPackage : null;
        };

        const _getCurrentPackage = () => {
            return _getPackageById(_currentPackageId);
        };

        const _getSelectedPackage = () => {
            return _getPackageById(self.selectedPackageId());
        };

        const _getPackageOnboardingComplete = (currentPackageId, selectedPackageId) => {
            const selectedPackage = _getPackageById(selectedPackageId);
            if (!selectedPackage) {
                return false;
            }

            const isOnboardingRequired = selectedPackage.isOnboardingRequired;
            if (!isOnboardingRequired){
                return true;
            }

            const isAddingPackage = currentPackageId === null && selectedPackageId !== null;
            if (isAddingPackage) {
                return false;
            }

            return _isPackageOnboardingComplete;
        };

        const _showCustomPlanModal = () => {
            return _promiseFactory.deferIndefinitely((customPlanPromise) => {
                _actionModal
                    .clearModal()
                    .setContentViewModel(AddCustomPlanActionModalViewModel, [])
                    .setHeaderText({i18n: 'addCustomPlanActionModal:headerTitle'})
                    .setShouldDisplayCancelButton(true)
                    .setShouldDisplaySubmitButton(false)
                    .setShouldScrollIntoView(false)
                    .setIsScrollDisabled(true);
                _actionModal.shouldDisplayDefaultFooterMessage(false);
                _actionModal.showModal()
                    .fail(customPlanPromise.reject)
                    .done(customPlanPromise.resolve);
            });
        };

        const _showPlanConfirmationModal = () => {
            return _promiseFactory.deferIndefinitely((confirmationPromise) => {
                 const constructorParams = [
                     _getCurrentPackage(),
                     _getSelectedPackage(),
                     _isPackageOnboardingComplete,
                     _updatePackage
                 ];

                _actionModal
                    .clearModal()
                    .setContentViewModel(AddPlanActionModalViewModel, constructorParams)
                    .setHeaderText({i18n: 'addPlanActionModal:headerTitle'})
                    .setShouldDisplayCancelButton(true)
                    .setShouldDisplaySubmitButton(false)
                    .setSubmitButtonText({i18n: 'confirm'})
                    .setShouldScrollIntoView(false)
                    .setIsScrollDisabled(true);
                _actionModal.shouldDisplayDefaultFooterMessage(true);
                _actionModal.showModal()
                    .fail(confirmationPromise.reject)
                    .done(confirmationPromise.resolve);
            });
        };

        const _updatePackage = () => {
            return _promiseFactory.deferWithMinimumWait((promise) => {
                const selectedPackageId = self.selectedPackageId();
                const isOnboardingComplete = _getPackageOnboardingComplete(_currentPackageId,selectedPackageId);

                _facade.updatePackage(self.packageType(), selectedPackageId, isOnboardingComplete)
                    .fail(promise.reject)
                    .done(() => {
                        self.modelStateObserver.saveData();

                        _currentPackageId = selectedPackageId;
                        _isPackageOnboardingComplete = isOnboardingComplete;

                        _modalService.closeModal(_actionModal);
                        _router.navigate(self.plansUrl);

                        promise.resolve();
                    });
            });
        };

        self.modelStateObserver = null;

        self.packageType = ko.observable(null);
        self.currentPackageName = ko.observable("");
        self.showLegacyPackageMessage = ko.observable(false);
        self.availablePackages = ko.observableArray([]);
        self.selectedPackageId = ko.observable(null).extend({observeState: true});

        self.headerI18n = ko.pureComputed(() => {
            switch (self.packageType()) {
                case _billingPackageTypes.telephony.id:
                    return { key: 'addPlan:title', options: { type: _billingPackageTypes.telephony.name } };
                case _billingPackageTypes.liveReceptionist.id:
                    return { key: 'addPlan:title', options: { type: _billingPackageTypes.liveReceptionist.name } };
                case _billingPackageTypes.webChat.id:
                    return { key: 'addPlan:title', options: { type: _billingPackageTypes.webChat.name } };
                case _billingPackageTypes.outboundCalling.id:
                    return { key: 'addPlan:title', options: { type: _billingPackageTypes.outboundCalling.name } };
                default:
                    return "";
            }
        });

        self.meteredBillingFAQ = ko.pureComputed(() => {
            return _buildFAQGroup(FAQ_GROUP.meteredBilling);
        });
        self.addPackageFAQ = ko.pureComputed(() => {
            return _buildFAQGroup(FAQ_GROUP.addingPlans);
        });
        self.changePackageFAQ = ko.pureComputed(() => {
            return _buildFAQGroup(FAQ_GROUP.changingPlans);
        });
        self.removePackageFAQ = ko.pureComputed(() => {
            return _buildFAQGroup(FAQ_GROUP.removingPlans);
        });

        self.plansUrl = _navigationConfiguration.routesById.plans.url;

        self.onCustomPlanSelect = () => {
            _showCustomPlanModal();
        };

        self.cancel = () => {
            self.selectedPackageId(_currentPackageId);
            self.modelStateObserver.restoreData();
            _router.navigate(self.plansUrl);
        };

        self.save = () => {
            return _promiseFactory.deferIndefinitely((promise) => {
                _showPlanConfirmationModal();
                promise.resolve();
            });
        };

        self.activate = (packageTypeKey) => {
            const FacadeConstructor = require('presentation/settings/billing/plans/facades/addPlanFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ModalServiceConstructor = require('presentation/common/modal');
            _modalService = new ModalServiceConstructor();

            self.modelStateObserver = new ModelStateObserverConstructor(self);

            return _initialize(packageTypeKey);
        };

        const _initialize = (packageTypeKey) => {

            let initPromiseFactory = new PromiseFactoryConstructor();

            switch (packageTypeKey) {
                case _billingPackageTypes.telephony.key:
                    self.packageType(_billingPackageTypes.telephony.id);
                    break;
                case _billingPackageTypes.liveReceptionist.key:
                    self.packageType(_billingPackageTypes.liveReceptionist.id);
                    break;
                case _billingPackageTypes.webChat.key:
                    self.packageType(_billingPackageTypes.webChat.id);
                    break;
                case _billingPackageTypes.outboundCalling.key:
                    self.packageType(_billingPackageTypes.outboundCalling.id);
                    break;
                default:
                    break;
            }

            initPromiseFactory.defer((billingPackagePromise) => {
                _facade.getAvailablePackages(self.packageType())
                    .fail(billingPackagePromise.reject)
                    .done((availablePackages) => {
                        self.availablePackages(availablePackages);
                        billingPackagePromise.resolve();
                    });
            });

            initPromiseFactory.defer((currentPackagePromise) => {
                _facade.getCurrentPackage(self.packageType())
                    .fail(currentPackagePromise.reject)
                    .done(({ billingPackage, isOnboardingComplete }) => {
                        if (billingPackage) {
                            _currentPackageId = billingPackage.packageId;
                            _isPackageOnboardingComplete = isOnboardingComplete;
                            self.selectedPackageId(billingPackage.packageId);
                            self.currentPackageName(billingPackage.name);
                            self.showLegacyPackageMessage(billingPackage.isRetired);
                        }
                        currentPackagePromise.resolve();
                    });
            });

            _promiseFactory.defer((initializePromise) => {
                initPromiseFactory.wait()
                    .done(() => {
                        self.modelStateObserver.commitData();
                        initializePromise.resolve();
                    });
            });

            return _promiseFactory.wait();
        };
    };
});
